import React from 'react';

import { AiDetailedFeedback, AiFeedbackTypes } from '@lumapps/ai/types';
import { isNegativeAiFeedback } from '@lumapps/ai/utils';
import { lumappsUserFeedbackPlugin } from '@lumapps/analytics-feedbacks/tracking';
import { LumappsTrackingAskReaction, TargetType } from '@lumapps/analytics-feedbacks/types';
import { formatSearchResultsForFeedbackAnalytics } from '@lumapps/analytics-feedbacks/utils/formatResultsForFeedbackAnalytics';

import type { AskSearchBestAnswer } from '../types';

interface UseAskAiFeedbackProps {
    answer: AskSearchBestAnswer;
    /** Unique ID to identify each search */
    traceId: string;
    /** Element to set the focus on when feedback closes */
    parentElement?: React.RefObject<HTMLElement>;
}

export const useAskAiFeedback = ({ traceId, answer, parentElement }: UseAskAiFeedbackProps) => {
    const [isFeedbackReasonsBlockOpen, setIsFeedbackReasonsOpen] = React.useState(false);
    const [currentFeedbackType, setCurrentFeedbackType] = React.useState<AiFeedbackTypes | undefined>(undefined);

    const handleClose = () => {
        /** Focus on the given element on close */
        if (parentElement) {
            parentElement.current?.focus();
        }
        setIsFeedbackReasonsOpen(false);
    };

    const sendFeedback = (feedback: AiDetailedFeedback) => {
        const basePayload: LumappsTrackingAskReaction = {
            traceId,
            reactionType: feedback.type,
            type: TargetType.ASK_REACTION,
            sources: formatSearchResultsForFeedbackAnalytics(answer.sources),
        };

        const trackingPayload: LumappsTrackingAskReaction = isNegativeAiFeedback(feedback)
            ? {
                  ...basePayload,
                  feedbackReason: feedback.reason,
                  comment: feedback.comment,
              }
            : basePayload;

        /** Send analytics event to better understand the reason behind the reaction */
        lumappsUserFeedbackPlugin.sendUserFeeback({
            payload: {
                action: trackingPayload,
            },
        });
    };

    const onFeedbackClick = (feedbackType: AiFeedbackTypes) => {
        setCurrentFeedbackType(feedbackType);

        sendFeedback({ type: feedbackType });

        if (feedbackType === AiFeedbackTypes.DISLIKED) {
            setIsFeedbackReasonsOpen(true);
        }
    };

    return {
        isFeedbackReasonsBlockOpen,
        feedbackType: currentFeedbackType,
        onFeedbackClick,
        sendFeedback,
        closeFeedbackReason: handleClose,
    };
};
